import { createStore } from '@stencil/store';
import { ToolbarState, SelectionState, TextEditingState, PageSelectionState, CropState } from '..';
import { FilePageDoc, PhotoDoc, ScrapbookElementDoc, ScrapbookElementType, ScrapbookReferenceType } from 'common';

export enum NavigationItem {
  HANDTOOL = 'handtool',
  MOVE = 'move',
}
export interface NavigationState {
  selectedNavigationItem: NavigationItem;
}

export interface RfiEditingState {
  isEditing: boolean;
  editingElementId: string | null;
  editingName: string;
  editingReferenceId: string | null;
  editingReferenceType: ScrapbookReferenceType | null;
  editingUrl: string | null;
}

interface PhotoMediaSelectionState {
  selectedMedia: PhotoDoc[];
  selectedMediaType: ScrapbookElementType.IMAGE;
}

interface DrawingMediaSelectionState {
  selectedMedia: FilePageDoc;
  selectedMediaType: ScrapbookElementType.DRAWING;
}

interface NullMediaSelectionState {
  selectedMedia: null;
  selectedMediaType: null;
}
export type MediaSelectionState = DrawingMediaSelectionState | PhotoMediaSelectionState | NullMediaSelectionState;
export interface MarkupsStoreState {
  toolbarState: ToolbarState;
  selectionState: SelectionState;
  textEditingState: TextEditingState;
  rfiEditingState: RfiEditingState;
  pageSelectionState: PageSelectionState;
  mediaSelectionState: MediaSelectionState;
  cropState: CropState;
  currentColor: string;
  navigationState: NavigationState;
  initialPageScale: number;
  markupsElements: ScrapbookElementDoc[];
  markupsElementsToRender: ScrapbookElementDoc[];
}

const initialStoreState: MarkupsStoreState = {
  toolbarState: {
    selectedMenuItemType: null,
    isDragDrawing: false,
    dragStartPosition: null,
    isCloudMarkup: false,
  },
  selectionState: {
    selectedElementId: null,
    selectedPageId: null,
    showElementToolbar: false,
    elementToolbarPosition: { x: 0, y: 0 },
    showEdgeHandles: false,
    edgeHandles: null,
  },
  mediaSelectionState: {
    selectedMedia: null,
    selectedMediaType: null,
  },
  textEditingState: {
    isEditing: false,
    editingElementId: null,
    editingText: '',
  },
  rfiEditingState: {
    isEditing: false,
    editingElementId: null,
    editingName: '',
    editingReferenceId: null,
    editingReferenceType: null,
    editingUrl: null,
  },
  pageSelectionState: {
    pageSelectionMode: null,
    pendingMediaCallback: null,
  },
  cropState: {
    cropMode: false,
    cropElementId: null,
    cropArea: null,
  },
  currentColor: '#000000',
  navigationState: {
    selectedNavigationItem: NavigationItem.HANDTOOL,
  },
  initialPageScale: 1,
  markupsElements: [],
  markupsElementsToRender: [],
};

export const { state: markupsState, onChange: markupsStoreOnChange, dispose: disposeMarkupsStore } = createStore<MarkupsStoreState>(initialStoreState);

// Helper functions to update store
export const setToolbarState = (newState: ToolbarState) => {
  markupsState.toolbarState = newState;
};

export const setSelectionState = (newState: SelectionState) => {
  markupsState.selectionState = newState;
};

export const resetSelectionState = () => {
  markupsState.selectionState = { ...initialStoreState.selectionState };
};

export const setTextEditingState = (newState: TextEditingState) => {
  markupsState.textEditingState = newState;
};

export const setRfiEditingState = (newState: RfiEditingState) => {
  markupsState.rfiEditingState = newState;
};

export const setPageSelectionState = (newState: PageSelectionState) => {
  markupsState.pageSelectionState = newState;
};

export const setCropState = (newState: CropState) => {
  markupsState.cropState = newState;
};

export const setCurrentColor = (color: string) => {
  markupsState.currentColor = color;
};

export const setNavigationState = (newState: NavigationState) => {
  markupsState.navigationState = newState;
};

/**
 * When a markup placement/draw tool is active, panzoom must not start a canvas pan on mousedown
 * over a page (otherwise the hand tool / pan steals the gesture before drag-to-draw runs).
 */
export function shouldSuppressPanzoomMouseDownForActiveMarkupTool(e: MouseEvent): boolean {
  const menuType = markupsState.toolbarState.selectedMenuItemType;
  if (menuType == null || menuType === ScrapbookElementType.FILTER) {
    return false;
  }
  const target = e.target as Element | null;
  return !!target?.closest?.('.page-group');
}

export const isInPageSelectionMode = (): boolean => {
  return markupsState.pageSelectionState.pageSelectionMode !== null;
};

export const setMediaSelectionState = (newState: MediaSelectionState) => {
  markupsState.mediaSelectionState = newState;
};

export const setInitialPageScale = (scale: number) => {
  markupsState.initialPageScale = scale;
};
export const isInMediaSelectionMode = (): boolean => {
  return markupsState.mediaSelectionState.selectedMedia !== null && markupsState.mediaSelectionState.selectedMediaType !== null;
};

export const setMarkupsElements = (elements: ScrapbookElementDoc[]) => {
  markupsState.markupsElements = elements;
};

export const setMarkupsElementsToRender = (elements: ScrapbookElementDoc[]) => {
  markupsState.markupsElementsToRender = elements;
};

export const resetStoreState = () => {
  markupsState.toolbarState = { ...initialStoreState.toolbarState };
  markupsState.selectionState = { ...initialStoreState.selectionState };
  markupsState.textEditingState = { ...initialStoreState.textEditingState };
  markupsState.rfiEditingState = { ...initialStoreState.rfiEditingState };
  markupsState.pageSelectionState = initialStoreState.pageSelectionState;
  markupsState.cropState = { ...initialStoreState.cropState };
  markupsState.currentColor = initialStoreState.currentColor;
  markupsState.navigationState = initialStoreState.navigationState;
  markupsState.mediaSelectionState = { ...initialStoreState.mediaSelectionState };
  markupsState.initialPageScale = initialStoreState.initialPageScale;
  markupsState.markupsElements = [];
  markupsState.markupsElementsToRender = [];
};
